import React, { useEffect } from 'react';
import { AuthProvider, LightHeader } from './HeaderAndNavigationBar';
import mixpanel from 'mixpanel-browser';
import { Container, DependencyContainerContext, useInstance } from '@meraki-internal/react-dependency-injection';
import { WebsiteDataV3 } from './data';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { createHashRouter, RouterProvider } from "react-router-dom";
import '@ionic/react/css/core.css';
import './ionic.css';
import { IonApp, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonContent, IonItem, IonLabel, IonList, IonNote, IonPage, IonText, setupIonicReact } from '@ionic/react';
import ErrorPage from './ErrorPage';
import { Home } from './Home';
import { EditClubHealthPage } from './clubs/EditClubHealthPage';
import { ClubPage } from './clubs/ClubPage';
import { ClubsState } from './clubs/ClubsState';
import { HTTPClient } from './support/api/HTTPClient';
import { Image } from './volunteers/Image';
import { EditRafflePage, RafflePage } from './raffle/RafflePage';

interface ISponsorLevel {
    name: string;
    level: number;
    descriptionHTML: string;
    availability: string;
    exclusivity: string;
}

const levels: ISponsorLevel[] = [
    {
        name: 'Founding Team Sponsor',
        level: 5000,
        descriptionHTML: 'This is a donation to <strong>fully fund a new team</strong>, which can be targeted to a specific town, and deferred until that town is ready to start. There will be a 20”x30” sign posted at their robotics lab for 2 years, with your sponsorship taking up at least 20% of that space. They will have a 20”x30” sign present at their location in the pit at every tournament for 2 years, with your sponsorship taking up at least 20% of that space. Your brand will be included on blackhillsrobotics.org sponsor list for 2 years.',
        exclusivity: 'This sponsorship is exclusive. There will only be one Founding Team Sponsorship per team, ever!',
        availability: `We don’t have any teams ready to go yet, but we expect to have more than we can fund by the end of the season. We are looking for at least 3 Founding Team Sponsors so we're ready when they are.`
    },
    {
        name: 'Raffle Sponsor',
        level: 2500,
        descriptionHTML: 'Your brand will be featured on our raffle signs visible at tournaments, on our raffle handouts, and on the raffle site. When we announce the winners, your brand will be thanked for making it possible. Your brand will be included on blackhillsrobotics.org sponsor list for 1 year.',
        exclusivity: 'This sponsorship is exclusive (there will only be one per raffle, we only expect to run one raffle per year).',
        availability: `We are looking for two sponsors, one for the current season (2024/2025) and next season (2025/2026)`
    },
    {
        name: 'Team Sponsor',
        level: 2000,
        descriptionHTML: 'Your brand will be featured on a 20x30 sign in the Robotics Lab for that team all season, with your brand taking up at least 20% of that sign. They will have a 20”x30” sign present at their location in the pit at every tournament for the entire season, with your brand taking up at least 20% of that sign. Your brand will be included on blackhillsrobotics.org sponsor list for 1 year.',
        exclusivity: 'There will be one sponsor per team.',
        availability: `We are looking for sponsors for the following teams: HS Spearfish, MS Spearfish x 2 teams, HS Douglas x 2 teams, MS Douglas x 2 teams`
    },
    {
        name: 'Tournament Field Sponsor',
        level: 1500,
        descriptionHTML: 'This sponsorship puts your brand front and center at the tournament. It puts your brand into the language of the tournament as teams are assigned to your field for their upcoming match or skills run. Your brand will get a special thank you during the tournament from the tournament MC of Event Planner. Your brand will be included on blackhillsrobotics.org sponsor list for 1 year.',
        exclusivity: 'There will be one sponsor per field and only 3 fields per tournament.',
        availability: `We are looking for sponsors for the following tournaments: Douglas Oct 26 x3, Spearfish Feb 15 x3`
    },
    {
        name: 'Tournament Lunch Sponsor',
        level: 1000,
        descriptionHTML: 'This sponsorship provides food for all the students, coaches and volunteers that make the tournament possible. You will get a 20x30 sign at the tournament with your brand taking up 50% of that sign. Your brand will get a special thank you during the tournament from the tournament MC of Event Planner. Your brand will be included on blackhillsrobotics.org sponsor list for 1 year.',
        exclusivity: 'There will be one sponsor per tournament.',
        availability: `We are looking for sponsors for the following tournaments: Douglas Oct 26, Spearfish Feb 15`
    },
    {
        name: 'Tournament Volunteers Sponsor',
        level: 750,
        descriptionHTML: 'This sponsorship provides t shirts to volunteers at both the Douglas and Spearfish tournament. To thank them for helping out, and so that students, parents, and coaches, know who the official volunteers are at the tournament. Your brand will get a special thank you during the tournament from the tournament MC of Event Planner. Your brand will be included on blackhillsrobotics.org sponsor list for 1 year.',
        exclusivity: 'There will be one sponsor for the season, sponsoring t shirts for volunteers for both of our local tournaments.',
        availability: `We are looking for a sponsor for the 2024/2025 season (High Stakes) and the 2025/2026 season.`
    },
    {
        name: 'Gold Sponsor',
        level: 500,
        descriptionHTML: 'Your brand will be featured on blackhillsrobotics.org for the 2024/2025 season (or 12 months, whichever is longer)',
        exclusivity: 'This sponsorship level is not exclusive.',
        availability: ''
    },
    {
        name: 'Silver Sponsor',
        level: 250,
        descriptionHTML: 'Your brand will be included on blackhillsrobotics.org sponsor list for 6 months.',
        exclusivity: 'This sponsorship level is not exclusive.',
        availability: ''
    },
    {
        name: 'Bronze Sponsor',
        level: 100,
        descriptionHTML: 'Your brand will be included on blackhillsrobotics.org sponsor list for one month.',
        exclusivity: 'This sponsorship level is not exclusive.',
        availability: ''
    }
]

const SponsorLevels: React.FC = () => {
    return (
        <div>
            <LightHeader title='Sponsor Levels' />
            <IonList>
                {levels.map(level => (
                    <IonItem key={level.name} style={{ marginTop: 20 }}>
                        <div style={{ display: 'flex', alignContent: 'start', gap: 10 }}>
                            <div>${level.level.toLocaleString()}</div>
                            <div>
                                <h1 style={{ fontWeight: 'bold', fontSize: 24 }}>{level.name}</h1>
                                <p>
                                    <IonText color="medium">
                                        <span dangerouslySetInnerHTML={{ __html: level.descriptionHTML }} />
                                    </IonText>
                                </p>
                                <p style={{ marginTop: 10 }}>
                                    <IonText color="medium">
                                        <span dangerouslySetInnerHTML={{ __html: level.exclusivity }} />
                                    </IonText>
                                </p>
                                <p style={{ marginTop: 10 }}>
                                    <IonText color="medium" style={{ fontStyle: 'italic' }} >
                                        {level.availability}
                                    </IonText>
                                </p>

                            </div>
                        </div>


                    </IonItem>
                ))}

            </IonList>
            <p style={{ padding: 20, paddingBottom: 100 }}>
                Interested in sponsorship, please contact <strong>Jon Nelson</strong> <a href="mailto:jon.nelson@blackhillsrobotics.org">jon.nelson@blackhillsrobotics.org</a> or at  <a href="tel:+16056419755">605-641-9755</a>
            </p>
        </div>
    );
}

setupIonicReact();

// TODO: better use of layout, nested routes and outlet
const router = createHashRouter([
    {
        path: "/",
        element: <Home />,
        errorElement: <ErrorPage />
    },
    {
        path: "/sponsor-levels",
        element: <SponsorLevels />,
        errorElement: <ErrorPage />
    },
    {
        path: "/clubs/:clubId/health/edit",
        element: (
            <IonApp>
                <EditClubHealthPage />
            </IonApp>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: "/clubs/:clubId/",
        element: (
            <IonApp>
                {/* TODO: how does the user get here? */}
                {/* TODO lighter header */}
                <ClubPage />
                {/* TODO: footer? */}
            </IonApp>
        ),
        errorElement: <ErrorPage />
    },
    {
        path: '/raffle/',
        element: (
            <RafflePage />
        )
    },
    {
        path: '/edit-raffle/',
        element: (
            <EditRafflePage />
        )
    }
]);



const CustomScriptAdapter: React.FC = () => {
    useEffect(() => {
        (window as any).customScript();
    }, [])
    return null;
}

// TODO: move to its own file
// TODO: use state to avoid reload
// TODO: shouldn't get warnings when logging in
// TODO: use identity token with API
const AuthProviderBinder: React.FC = () => {
    const { logout, getIdTokenClaims, loginWithPopup } = useAuth0();

    const authProvider = useInstance(AuthProvider);
    authProvider.auth0 = {
        logout, getIdTokenClaims, loginWithPopup
    }
    return null;
};

class BHRFAppContainer extends DependencyContainerContext {
    containerMounted = async (container: Container) => {
        // TODO: error handling
        // TODO: local DX
        const data = await fetch('https://static-api.blackhillsrobotics.org/blackhills-robotics/blackhills-robotics-v3.json?cache-bust=4').then(res => res.json());

        container.registerInstance(WebsiteDataV3, data)

        const authProvider = container.get(AuthProvider);
        authProvider.init();

        if (authProvider.idToken) {
            container.registerInstance(HTTPClient, new HTTPClient({
                // baseURL: 'http://localhost:8000',
                baseURL: 'https://oah29udth6.execute-api.us-east-1.amazonaws.com/v1',
                accessToken: authProvider.idToken
            }));
        }
        // TODO: ApiClient / HTTPClient should throw

        container.get(ClubsState).init().catch(err => {
            console.log(err);
        })
    }
}

export const App: React.FC = () => {
    useEffect(() => {
        if (window.location.hostname !== 'localhost') {
            mixpanel.init("e566ebbb63a25ef54315d6fea6dc6c79", {
                debug: false,
                track_pageview: true,
                persistence: "localStorage",
            });
        }
    });

    return (
        <BHRFAppContainer>
            <Auth0Provider
                domain="dev-7k2gbglgzgheruzn.us.auth0.com"
                clientId="VsMVs6hwCNizVDuz5THsuWatBNN8D9Wc"
                authorizationParams={{
                    redirect_uri: window.location.origin
                }}
            >
                <AuthProviderBinder />

                <CustomScriptAdapter />

                <RouterProvider router={router} />

            </Auth0Provider>

        </BHRFAppContainer>
    );
}
